import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
// Self-hosted Inter Variable (latin subset, weight axis). Importing the
// woff2 with ?url gives us the same hashed asset URL Vite emits, so the
// <link rel="preload"> below matches the @font-face src exactly and the
// browser kicks off the font request alongside (not after) the CSS.
import interLatinWoff2 from "@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Toaster } from "@/components/ui/sonner";
import { CookieBanner } from "@/components/site/CookieBanner";
import { WebVitalsReporter } from "@/components/site/WebVitalsReporter";
import { TawkChat } from "@/components/site/TawkChat";

import { supabase } from "@/integrations/supabase/client";
import { initSentryClient, captureClientException, setClientRequestId } from "@/lib/sentry-client";
import { REQUEST_ID_HEADER, generateRequestId } from "@/lib/request-id";

// Initialize Sentry as early as possible on the client.
if (typeof window !== "undefined") {
  initSentryClient();
  // Patch fetch once to propagate a per-tab correlation ID.
  const w = window as unknown as { __reqIdPatched?: boolean; __requestId?: string };
  if (!w.__reqIdPatched) {
    w.__reqIdPatched = true;
    w.__requestId = generateRequestId();
    setClientRequestId(w.__requestId);
    const origFetch = window.fetch.bind(window);
    window.fetch = (input, init = {}) => {
      const headers = new Headers(init.headers as HeadersInit | undefined);
      if (!headers.has(REQUEST_ID_HEADER)) headers.set(REQUEST_ID_HEADER, w.__requestId!);
      return origFetch(input, { ...init, headers });
    };
  }
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link to="/" className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90">
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
    captureClientException(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  const requestId = typeof window !== "undefined"
    ? (window as unknown as { __requestId?: string }).__requestId
    : undefined;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">This page didn't load</h1>
        <p className="mt-2 text-sm text-muted-foreground">Something went wrong. Try refreshing or go back home.</p>
        {requestId && (
          <p className="mt-2 text-xs text-muted-foreground/70">Reference: <code>{requestId}</code></p>
        )}
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >Try again</button>
          <a href="/" className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent">Go home</a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Peopels — Hire local Helpers across Greater London" },
      { name: "description", content: "Greater London's trusted marketplace for vetted local Helpers. Get help with handyman tasks, TV mounting, furniture assembly and moving — book in minutes." },
      { name: "author", content: "Peopels" },
      { property: "og:title", content: "Peopels — Hire local Helpers across Greater London" },
      { property: "og:description", content: "Greater London's trusted marketplace for vetted local Helpers." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Peopels" },
      // Icon-only social mark — renders correctly as the profile image on
      // Facebook, Instagram, LinkedIn, X, TikTok and YouTube link previews.
      // ?v=2 forces platform debuggers to fetch the refreshed artwork
      // instead of serving the previously cached share card.
      { property: "og:image", content: "https://peopels.com/android-chrome-512x512.png?v=2" },
      { property: "og:image:width", content: "512" },
      { property: "og:image:height", content: "512" },
      { property: "og:image:alt", content: "Peopels" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:site", content: "@peopels" },
      { name: "twitter:creator", content: "@peopels" },
      { name: "twitter:image", content: "https://peopels.com/android-chrome-512x512.png?v=2" },
      { name: "twitter:image:alt", content: "Peopels" },
      { name: "theme-color", content: "#2563EB" },
    ],
    links: [
      // Preload the Inter Variable woff2 so it starts downloading in
      // parallel with the CSS and is ready when the parser hits the
      // inline @font-face below — keeps LCP text rendered in Inter
      // instead of system fallback (avoids late font swap / CLS).
      { rel: "preload", as: "font", type: "font/woff2", href: interLatinWoff2, crossOrigin: "anonymous" },
      { rel: "stylesheet", href: appCss },
      // Peopels favicon system — icon-only mark across sizes for crisp
      // rendering in browser tabs, iOS home-screen, and Android PWA.
      // ?v=2 busts stale browser/CDN caches after the brand refresh.
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico?v=2" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16x16.png?v=2" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png?v=2" },
      { rel: "icon", type: "image/png", sizes: "48x48", href: "/favicon-48x48.png?v=2" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png?v=2" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/android-chrome-192x192.png?v=2" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/android-chrome-512x512.png?v=2" },
      { rel: "manifest", href: "/site.webmanifest?v=2" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://peopels.com/#organization",
              name: "Peopels",
              url: "https://peopels.com",
              logo: "https://peopels.com/android-chrome-512x512.png?v=2",
              sameAs: ["https://twitter.com/peopels"],
              areaServed: { "@type": "AdministrativeArea", name: "Greater London" },
            },
            {
              "@type": "WebSite",
              "@id": "https://peopels.com/#website",
              url: "https://peopels.com",
              name: "Peopels",
              publisher: { "@id": "https://peopels.com/#organization" },
              potentialAction: {
                "@type": "SearchAction",
                target: "https://peopels.com/browse?q={search_term_string}",
                "query-input": "required name=search_term_string",
              },
            },
          ],
        }),
      },
    ],
  }),

  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  // Inline @font-face so the browser can match the preloaded woff2 and
  // register the family without waiting for the external stylesheet to
  // parse. font-display: swap → text paints in the system fallback
  // immediately, then re-renders in Inter once available (no FOIT).
  const fontFaceCss = `@font-face{font-family:'Inter Variable';font-style:normal;font-display:swap;font-weight:100 900;src:url(${interLatinWoff2}) format('woff2-variations');unicode-range:U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD;}`;
  return (
    <html lang="en">
      <head>
        <HeadContent />
        <style dangerouslySetInnerHTML={{ __html: fontFaceCss }} />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
      router.invalidate();
      if (event !== "SIGNED_OUT") queryClient.invalidateQueries();
    });
    // Register the offline service worker (no-op in dev / Lovable preview).
    void import("@/lib/pwa-register").then((m) => m.registerAppPwa()).catch(() => {});
    return () => sub.subscription.unsubscribe();
  }, [router, queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
      <Toaster richColors position="top-center" />
      <CookieBanner />
      <WebVitalsReporter />
      <TawkChat />
    </QueryClientProvider>
  );

}
